// =============================================================================
// StudioSection — JEDEN wzor karty sekcji w Studio /strony/:websiteId.
// Naglowek h-12 z border-b: ikona · tytul · badge · (streszczenie) po lewej,
// akcje po prawej; opcjonalnie zwijana ([▾]); tresc pelnej szerokosci;
// stopka (np. „Zapisz…" po prawej). Pomoc = ikona „?" z tooltipem przy tytule.
// =============================================================================

import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, HelpCircle, type LucideIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface Props {
  icon: LucideIcon;
  title: string;
  /** Badge tuz za tytulem (zrodlo AI, status, licznik) */
  badge?: ReactNode;
  /** Streszczenie w naglowku (chipy, liczby) — dla sekcji zwijanych */
  summary?: ReactNode;
  /** Akcje po prawej stronie naglowka */
  actions?: ReactNode;
  help?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Stopka sekcji (border-t), tresc wyrownana do prawej */
  footer?: ReactNode;
  children?: ReactNode;
  id?: string;
}

export function StudioSection({ icon: Icon, title, badge, summary, actions, help, collapsible = false, defaultOpen = true, footer, children, id }: Props) {
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);
  const expandable = collapsible && Boolean(children);
  const showBody = Boolean(children) && (!collapsible || open);
  return (
    <section id={id} className="w-full rounded-lg border bg-card">
      <div className="flex min-h-12 flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2">
        <button
          type="button"
          onClick={() => expandable && setOpen((o) => !o)}
          className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${expandable ? "hover:text-foreground" : "cursor-default"}`}
          aria-expanded={expandable ? open : undefined}
        >
          {expandable ? (open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : <Icon className="h-3.5 w-3.5" />}
          {title}
          {help && (
            <Tooltip>
              <TooltipTrigger asChild><HelpCircle className="h-3.5 w-3.5 opacity-60" /></TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs">{help}</TooltipContent>
            </Tooltip>
          )}
        </button>
        {badge}
        {summary && <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-sm">{summary}</div>}
        {actions && <div className={`flex flex-wrap items-center gap-2 ${summary ? "" : "ml-auto"}`}>{actions}</div>}
      </div>
      {showBody && <div className="px-4 py-4">{children}</div>}
      {showBody && footer && <div className="flex flex-wrap items-center justify-end gap-3 border-t px-4 py-3">{footer}</div>}
    </section>
  );
}
